/**
 * Helios Forensic Protocol - Black Box Telemetry System
 * Maximum Logging. Zero Assumptions.
 * 
 * This module instruments the application with deep observability:
 * - Network request/response tracking with performance metrics
 * - Console history buffer for crash analysis
 * - Firestore listener leak detection
 * - React render cycle monitoring
 */

// ============================================================
// TEST ENVIRONMENT DETECTION
// ============================================================

// Disable telemetry completely in test environments to prevent:
// - Jest OOM errors from interval timers
// - HELIOS banner noise during tests
// - Fetch wrapper interference with Firebase emulation
const isTestEnv = 
  typeof process !== 'undefined' && 
  (process.env.JEST_WORKER_ID !== undefined || 
   process.env.NODE_ENV === 'test' ||
   import.meta.env?.MODE === 'test');

// ============================================================
// PHASE 1: Global State Snapshot & Debug History
// ============================================================

interface LogEntry {
  timestamp: number;
  level: 'log' | 'warn' | 'error' | 'info';
  message: string;
  data?: unknown;
}

interface NetworkLog {
  timestamp: number;
  method: string;
  url: string;
  status?: number;
  duration?: number;
  error?: string;
  body?: unknown;
}

interface RenderLog {
  timestamp: number;
  component: string;
  renderCount: number;
  props?: Record<string, unknown>;
}

interface TelemetryState {
  debugHistory: LogEntry[];
  networkLogs: NetworkLog[];
  renderLogs: RenderLog[];
  activeListeners: number;
  renderCounts: Map<string, { count: number; lastReset: number }>;
}

// Global telemetry state - accessible via window.telemetry
const telemetryState: TelemetryState = {
  debugHistory: [],
  networkLogs: [],
  renderLogs: [],
  activeListeners: 0,
  renderCounts: new Map(),
};

// Expose to window for browser console access
declare global {
  interface Window {
    telemetry: TelemetryState;
    dumpTelemetry: () => void;
    clearTelemetry: () => void;
  }
}

if (typeof window !== 'undefined') {
  window.telemetry = telemetryState;
  
  // Helper: Dump all telemetry to console
  window.dumpTelemetry = () => {
    console.group('🔍 HELIOS TELEMETRY DUMP');
    console.log('Last 50 Console Logs:', telemetryState.debugHistory.slice(-50));
    console.log('Network Activity:', telemetryState.networkLogs.slice(-20));
    console.log('Render Activity:', telemetryState.renderLogs.slice(-20));
    console.log('Active Firestore Listeners:', telemetryState.activeListeners);
    console.log('Component Render Counts:', Object.fromEntries(telemetryState.renderCounts));
    console.groupEnd();
  };
  
  // Helper: Clear telemetry
  window.clearTelemetry = () => {
    telemetryState.debugHistory = [];
    telemetryState.networkLogs = [];
    telemetryState.renderLogs = [];
    telemetryState.renderCounts.clear();
    console.log('✅ Telemetry cleared');
  };
}

const MAX_LOG_ENTRIES = 50;
const MAX_NETWORK_LOGS = 100;
const MAX_RENDER_LOGS = 100;

function addLogEntry(level: LogEntry['level'], message: string, data?: unknown) {
  const entry: LogEntry = {
    timestamp: Date.now(),
    level,
    message,
    data,
  };
  
  telemetryState.debugHistory.push(entry);
  
  // Keep only last N entries
  if (telemetryState.debugHistory.length > MAX_LOG_ENTRIES) {
    telemetryState.debugHistory.shift();
  }
}

// ============================================================
// PHASE 1.1: Console Override (Flight Recorder)
// ============================================================

const originalConsole = {
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  info: console.info.bind(console),
};

if (!isTestEnv) {
  console.log = (...args: unknown[]) => {
    addLogEntry('log', args.map(a => String(a)).join(' '), args);
    originalConsole.log(...args);
  };

  console.warn = (...args: unknown[]) => {
    addLogEntry('warn', args.map(a => String(a)).join(' '), args);
    originalConsole.warn(...args);
  };

  console.error = (...args: unknown[]) => {
    addLogEntry('error', args.map(a => String(a)).join(' '), args);
    originalConsole.error(...args);
  };

  console.info = (...args: unknown[]) => {
    addLogEntry('info', args.map(a => String(a)).join(' '), args);
    originalConsole.info(...args);
  };
}

// ============================================================
// PHASE 1.2: Network Interceptor (Global Fetch Override)
// ============================================================

const SLOW_REQUEST_THRESHOLD = 3000; // 3 seconds
const HANGING_REQUEST_THRESHOLD = 5000; // 5 seconds

// Firestore streaming endpoints that are expected to be long-lived
// These should NOT trigger deadlock warnings because they're intentionally persistent
const FIRESTORE_STREAMING_PATTERNS = [
  /google\.firestore\.v1\.Firestore\/Listen/,
  /google\.firestore\.v1\.Firestore\/Write/,
];

// Login bootstrap functions - expected on first page load (TASK D optimization)
const LOGIN_BOOTSTRAP_FUNCTIONS = [
  'ensureUserDoc',
  'onLoginSetClaims',
  'logAuthEvent',
];

/**
 * Check if a URL is a Firestore streaming endpoint that should not trigger deadlock warnings
 */
function isFirestoreStreamingRequest(url: string): boolean {
  return FIRESTORE_STREAMING_PATTERNS.some(pattern => pattern.test(url));
}

/**
 * Check if a URL is a login bootstrap function
 */
function isLoginBootstrapFunction(url: string): boolean {
  return LOGIN_BOOTSTRAP_FUNCTIONS.some(fn => url.includes(fn));
}

/**
 * Get human-readable label for network request
 */
function getRequestLabel(method: string, url: string): string {
  if (isLoginBootstrapFunction(url)) {
    const fnName = LOGIN_BOOTSTRAP_FUNCTIONS.find(fn => url.includes(fn)) || 'unknown';
    return `Login bootstrap – ${fnName}`;
  }
  return `${method} ${url}`;
}

if (!isTestEnv && typeof window !== 'undefined' && window.fetch) {
  // Capture native fetch BEFORE wrapping to prevent Firestore corruption
  const nativeFetch = window.fetch.bind(window);
  const originalFetch = window.fetch;
  
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [resource, config] = args;
    const url = typeof resource === 'string' 
      ? resource 
      : resource instanceof Request 
        ? resource.url 
        : resource.toString();
    
    // CRITICAL BYPASS: Firestore Listen/WebChannel must use unwrapped fetch
    // Any interception (body cloning, header mutation, timeout logic) breaks realtime listeners
    // STRICT PREDICATE (P0.1): Only bypass Firestore Listen endpoints, NOT all "webchannel" substrings
    try {
      const urlObj = new URL(url, window.location.origin);
      const isFirestoreHost = urlObj.hostname === 'firestore.googleapis.com';
      const isListenPath = 
        urlObj.pathname.includes('/google.firestore.v1.Firestore/Listen/channel') ||
        urlObj.pathname.includes('/google.firestore.v1.Firestore/Listen');
      
      if (isFirestoreHost && isListenPath) {
        if (import.meta.env.DEV) {
          console.debug('[Telemetry] Bypassing Firestore Listen request:', urlObj.pathname);
        }
        return nativeFetch(...args);
      }
    } catch (_urlParseError) {
      // If URL parsing fails, continue with telemetry (don't bypass)
      // This prevents accidental bypasses from malformed URLs
      if (import.meta.env.DEV) {
        console.warn('[Telemetry] URL parse failed, applying telemetry:', url);
      }
    }
    const method = config?.method || 'GET';
    const startTime = Date.now();
    
    const networkLog: NetworkLog = {
      timestamp: startTime,
      method,
      url,
    };
    
    // Sanitize body for logging
    let sanitizedBody: unknown = null;
    if (config?.body) {
      try {
        const bodyStr = typeof config.body === 'string' ? config.body : JSON.stringify(config.body);
        const parsed = JSON.parse(bodyStr) as Record<string, unknown>;
        
        // Remove sensitive fields
        if ('password' in parsed) parsed.password = '[REDACTED]';
        if ('token' in parsed) parsed.token = '[REDACTED]';
        if ('apiKey' in parsed) parsed.apiKey = '[REDACTED]';
        
        sanitizedBody = parsed;
      } catch {
        sanitizedBody = '[Non-JSON Body]';
      }
    }
    
    networkLog.body = sanitizedBody;
    
    // Hanging request detector - SKIP for Firestore streaming endpoints
    // Firestore Listen/Write are long-lived streaming connections, not hanging requests
    const isStreaming = isFirestoreStreamingRequest(url);
    const isBootstrap = isLoginBootstrapFunction(url);
    const requestLabel = getRequestLabel(method, url);
    const hangingStack = !isStreaming ? new Error('Fetch pending').stack : null;
    const hangingTimeout = isStreaming ? null : setTimeout(() => {
      const duration = Date.now() - startTime;
      console.error(
        `🔴 [POTENTIAL DEADLOCK] ${requestLabel} hanging for ${duration}ms`,
        { method, url, body: sanitizedBody, stack: hangingStack }
      );
    }, HANGING_REQUEST_THRESHOLD);
    
    try {
      const response = await originalFetch(...args);
      if (hangingTimeout) clearTimeout(hangingTimeout);
      
      const duration = Date.now() - startTime;
      networkLog.status = response.status;
      networkLog.duration = duration;
      
      // Log slow or failed requests (but not streaming requests, which are expected to be long)
      if (!isStreaming && duration > SLOW_REQUEST_THRESHOLD) {
        const logMessage = isBootstrap 
          ? `⏱️ [LOGIN BOOTSTRAP] ${requestLabel} took ${duration}ms (expected on cold start)`
          : `⚠️ [SLOW REQUEST] ${requestLabel} took ${duration}ms`;
        
        console.warn(logMessage, { status: response.status, body: sanitizedBody });
      }
      
      if (response.status >= 400) {
        console.error(
          `🔴 [HTTP ERROR] ${requestLabel} returned ${response.status}`,
          { duration, body: sanitizedBody }
        );
      }
      
      telemetryState.networkLogs.push(networkLog);
      if (telemetryState.networkLogs.length > MAX_NETWORK_LOGS) {
        telemetryState.networkLogs.shift();
      }
      
      return response;
    } catch (error) {
      if (hangingTimeout) clearTimeout(hangingTimeout);
      
      const duration = Date.now() - startTime;
      networkLog.duration = duration;
      networkLog.error = String(error);
      
      // Don't log AbortErrors for Firestore streaming - these are expected during navigation/unmount
      const isAbortError = error instanceof Error && 
                          (error.name === 'AbortError' || 
                           error.message.includes('signal is aborted'));
      
      if (!isStreaming || !isAbortError) {
        console.error(
          `🔴 [NETWORK FAILURE] ${requestLabel} failed after ${duration}ms`,
          { error, body: sanitizedBody }
        );
      }
      
      telemetryState.networkLogs.push(networkLog);
      if (telemetryState.networkLogs.length > MAX_NETWORK_LOGS) {
        telemetryState.networkLogs.shift();
      }
      
      throw error;
    }
  };
  
  console.info('✅ Helios Network Interceptor Active');
}

// ============================================================
// PHASE 2: Firestore Listener Leak Detection
// ============================================================

export function registerListener(): () => void {
  telemetryState.activeListeners++;
  console.log(`📡 Listener Registered | Active: ${telemetryState.activeListeners}`);
  
  return () => {
    telemetryState.activeListeners--;
    console.log(`📡 Listener Unsubscribed | Active: ${telemetryState.activeListeners}`);
  };
}

// Periodic listener audit
let listenerAuditInterval: NodeJS.Timeout | null = null;
if (!isTestEnv && typeof window !== 'undefined') {
  listenerAuditInterval = setInterval(() => {
    if (telemetryState.activeListeners > 0) {
      console.info(`📊 Active Listeners: ${telemetryState.activeListeners}`);
    }
    
    if (telemetryState.activeListeners > 20) {
      console.error(
        `🔴 [MEMORY LEAK DETECTED] ${telemetryState.activeListeners} active listeners! Possible unsubscribe failure.`
      );
    }
  }, 5000);
}

// ============================================================
// PHASE 3: React Render Cycle Monitor
// ============================================================

const RENDER_BURST_THRESHOLD = 4; // renders in 1 second
const RENDER_WINDOW = 1000; // 1 second

export function trackComponentRender(componentName: string, props?: Record<string, unknown>) {
  const now = Date.now();
  const tracking = telemetryState.renderCounts.get(componentName) || {
    count: 0,
    lastReset: now,
  };
  
  // Reset counter if window expired
  if (now - tracking.lastReset > RENDER_WINDOW) {
    tracking.count = 0;
    tracking.lastReset = now;
  }
  
  tracking.count++;
  telemetryState.renderCounts.set(componentName, tracking);
  
  // Log excessive renders
  if (tracking.count > RENDER_BURST_THRESHOLD) {
    console.error(
      `🔴 [INFINITE LOOP DETECTED] ${componentName} rendered ${tracking.count} times in ${RENDER_WINDOW}ms`,
      { props }
    );
    
    telemetryState.renderLogs.push({
      timestamp: now,
      component: componentName,
      renderCount: tracking.count,
      props,
    });
    
    if (telemetryState.renderLogs.length > MAX_RENDER_LOGS) {
      telemetryState.renderLogs.shift();
    }
  }
}

// ============================================================
// PHASE 4: State Snapshot Logger
// ============================================================

export function logStateChange(providerName: string, state: unknown) {
  // Sanitize state (remove functions, circular refs)
  let sanitizedState: unknown;
  try {
    sanitizedState = JSON.parse(JSON.stringify(state, (_key, value) => {
      if (typeof value === 'function') return '[Function]';
      if (value instanceof Error) return `[Error: ${value.message}]`;
      return value;
    }));
  } catch {
    sanitizedState = '[Circular Reference]';
  }
  
  console.log(`🔄 [${providerName}] State Change:`, sanitizedState);
  
  // Check for undefined critical values
  if (providerName === 'AuthProvider') {
    const authState = sanitizedState as Record<string, unknown>;
    if (authState.user === undefined && authState.loading === false) {
      console.error(
        `🔴 [AUTH FAILURE] User became undefined while loading=false`,
        sanitizedState
      );
    }
  }
  
  // NOTE: Stuck loading detection removed - handled by individual providers with proper state checking
}

// ============================================================
// Initialization
// ============================================================

if (!isTestEnv) {
  console.info(`
╔═══════════════════════════════════════════════════════════╗
║   🚀 HELIOS FORENSIC PROTOCOL - TELEMETRY ACTIVE          ║
║                                                           ║
║   Black Box Recording:     ✅ Last 50 console logs       ║
║   Network Monitoring:      ✅ Slow/failed requests       ║
║   Listener Leak Detection: ✅ Every 5 seconds            ║
║   Render Cycle Audit:      ✅ Infinite loop detection    ║
║                                                           ║
║   Browser Console Commands:                               ║
║   - window.dumpTelemetry()   → Full diagnostic dump      ║
║   - window.clearTelemetry()  → Reset all logs            ║
║   - window.telemetry         → Raw telemetry state       ║
╚═══════════════════════════════════════════════════════════╝
`);
}

/**
 * Stop all telemetry intervals and cleanup (for tests)
 */
export function stopTelemetry(): void {
  if (listenerAuditInterval) {
    clearInterval(listenerAuditInterval);
    listenerAuditInterval = null;
  }
}

/**
 * Reset render tracking state (for tests)
 */
export function resetRenderTracking(): void {
  telemetryState.renderCounts.clear();
  telemetryState.renderLogs = [];
}

export default {
  registerListener,
  trackComponentRender,
  logStateChange,
  stopTelemetry,
  resetRenderTracking,
};
